import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../../Auth/AuthContext';
import { getAuthenticatedTicketUser } from '../../../utils/ticketUser';
import { closePdfPopup, openPdfBlob, openPdfPopup } from '../../../utils/api';
import { authenticatedSessionRequestConfig } from '../../../utils/csrf';
import { formatApiMoneyForInput, parseColombianMoneyInput } from '../../../utils/money';
import {
  buildMonthlyEntryTicketCommand,
  canonicalTicketErrorMessage,
  createTicketOperationId,
  isTicketChangedError,
} from '../../../utils/monthlyTicketCommands';
import { monthlyTicketObservationsInputValue } from '../../../utils/monthlyTicketObservations';

const API_URL = process.env.REACT_APP_API_URL;
const ROWS_PER_PAGE = 5;

const effectiveMonthlyEntryValue = (data, latestRenewal) => (
  latestRenewal?.valorTicketModificado
  ?? latestRenewal?.valor
  ?? data?.valorTicketModificado
  ?? data?.valor
  ?? 0
);

const TicketEntradaForm = ({
  openTicketEntradaForm,
  setOpenTicketEntradaForm,
  data,
  areUserAdmin,
  handleUpdateData,
}) => {
  const { user } = useAuth();
  const submitRef = useRef(false);
  const operationIdRef = useRef(null);
  const cacheRef = useRef({});
  const tickets = useMemo(
    () => (Array.isArray(data?.ticketsMensualidad) ? data.ticketsMensualidad : []),
    [data]
  );
  const currentTicket = tickets.find(
    (ticket) => Number(ticket.id) === Number(data?.ticket_ingreso_canonico)
  ) || null;
  const latestRenewal = Array.isArray(data?.renovaciones) && data.renovaciones.length
    ? data.renovaciones[data.renovaciones.length - 1]
    : null;
  const [selectedCliente, setSelectedCliente] = useState(data?.cliente?.id || null);
  const [dataSelectedCliente, setDataSelectedCliente] = useState(data?.cliente || null);
  const [fechaInicio, setFechaInicio] = useState(
    currentTicket?.fecha_inicio ?? data?.fecha_inicio ?? ''
  );
  const [fechaFin, setFechaFin] = useState(
    currentTicket?.fecha_fin ?? data?.fecha_final ?? ''
  );
  const [valorPagado, setValorPagado] = useState(
    formatApiMoneyForInput(effectiveMonthlyEntryValue(data, latestRenewal))
  );
  const [selectedObservaciones, setSelectedObservaciones] = useState(data?.observaciones ?? '');
  const [ticketEdited, setTicketEdited] = useState(false);
  const [editing, setEditing] = useState(false);
  const [clientesData, setClientesData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [openDialogClientes, setOpenDialogClientes] = useState(false);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successWarning, setSuccessWarning] = useState('');
  const [refreshPending, setRefreshPending] = useState(false);

  useLayoutEffect(() => {
    if (!openTicketEntradaForm) return;
    if (currentTicket) {
      setDataSelectedCliente({ nombre: currentTicket.nombre_cliente, apellidos: '' });
      setSelectedCliente(null);
      setFechaInicio(currentTicket.fecha_inicio ?? data?.fecha_inicio ?? '');
      setFechaFin(currentTicket.fecha_fin ?? data?.fecha_final ?? '');
      setValorPagado(
        formatApiMoneyForInput(parseColombianMoneyInput(currentTicket.valor))
      );
      setSelectedObservaciones(currentTicket.observaciones ?? '');
    } else {
      setDataSelectedCliente(data?.cliente || null);
      setSelectedCliente(data?.cliente?.id || null);
      setFechaInicio(data?.fecha_inicio ?? '');
      setFechaFin(data?.fecha_final ?? '');
      setValorPagado(formatApiMoneyForInput(effectiveMonthlyEntryValue(data, latestRenewal)));
      setSelectedObservaciones(data?.observaciones ?? '');
    }
    setTicketEdited(false);
    setEditing(false);
    operationIdRef.current = null;
    setErrorMessage('');
    setSuccessWarning('');
    setRefreshPending(false);
  }, [currentTicket, data, latestRenewal, openTicketEntradaForm]);

  const restoreCurrentTicket = () => {
    if (!currentTicket) return;
    setDataSelectedCliente({ nombre: currentTicket.nombre_cliente, apellidos: '' });
    setSelectedCliente(null);
    setFechaInicio(currentTicket.fecha_inicio ?? data?.fecha_inicio ?? '');
    setFechaFin(currentTicket.fecha_fin ?? data?.fecha_final ?? '');
    setValorPagado(
      formatApiMoneyForInput(parseColombianMoneyInput(currentTicket.valor))
    );
    setSelectedObservaciones(currentTicket.observaciones ?? '');
    setTicketEdited(false);
  };

  useEffect(() => {
    if (!openDialogClientes) return undefined;
    let mounted = true;
    const fetchClientes = async () => {
      const cacheKey = `${searchText}|${currentPage}`;
      if (cacheRef.current[cacheKey]) {
        setClientesData(cacheRef.current[cacheKey].results);
        setTotalPages(Math.max(1, Math.ceil(cacheRef.current[cacheKey].count / ROWS_PER_PAGE)));
        return;
      }
      setLoadingClientes(true);
      try {
        const response = await axios.get(`${API_URL}/clientes/`, {
          params: { search: searchText, page: currentPage, page_size: ROWS_PER_PAGE },
        });
        if (!mounted) return;
        const results = response.data?.results || [];
        const count = response.data?.count || 0;
        cacheRef.current[cacheKey] = { results, count };
        setClientesData(results);
        setTotalPages(Math.max(1, Math.ceil(count / ROWS_PER_PAGE)));
      } catch (error) {
        if (mounted) setErrorMessage(canonicalTicketErrorMessage(error));
      } finally {
        if (mounted) setLoadingClientes(false);
      }
    };
    fetchClientes();
    return () => { mounted = false; };
  }, [currentPage, openDialogClientes, searchText]);

  const handleCrearTicket = async () => {
    if (submitRef.current || loading) return;
    let pdfWindow = null;
    let commandSucceeded = false;
    try {
      getAuthenticatedTicketUser(user);
      const operation = currentTicket ? (editing ? 'replace' : 'reprint') : 'issue';
      const snapshot = operation === 'reprint' ? undefined : {
        valor: parseColombianMoneyInput(valorPagado),
        observaciones: selectedObservaciones,
        ...(selectedCliente ? { cliente_id: selectedCliente } : {}),
        ...(operation === 'replace' ? {
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin,
        } : {}),
      };
      if (operation === 'replace' && !operationIdRef.current) {
        operationIdRef.current = createTicketOperationId();
      }
      const command = buildMonthlyEntryTicketCommand(
        data?.id,
        operation,
        snapshot,
        operation === 'replace' ? {
          expectedTicketId: currentTicket.id,
          operationId: operationIdRef.current,
        } : undefined
      );
      pdfWindow = openPdfPopup();
      if (!pdfWindow) return;
      submitRef.current = true;
      setLoading(true);
      setErrorMessage('');
      const requestConfig = await authenticatedSessionRequestConfig(API_URL, axios.get);
      const response = await axios.post(
        `${API_URL}/tickets-mensualidad/`,
        command,
        requestConfig
      );
      if (![200, 201].includes(response.status) || !response.data?.id) {
        throw new Error('El servidor no devolvió el ticket.');
      }
      commandSucceeded = true;
      operationIdRef.current = null;
      setEditing(false);
      setTicketEdited(false);
      try {
        const pdfResponse = await axios.get(
          `${API_URL}/tickets-mensualidad/${response.data.id}/`,
          { responseType: 'blob', withCredentials: true }
        );
        openPdfBlob(new Blob([pdfResponse.data], { type: 'application/pdf' }), pdfWindow);
      } catch (pdfError) {
        closePdfPopup(pdfWindow);
        setSuccessWarning('El nuevo ticket fue generado, pero no se pudo abrir el PDF. Actualiza los datos antes de continuar.');
        setRefreshPending(true);
        return;
      }
      try {
        if (handleUpdateData) await handleUpdateData();
        setOpenTicketEntradaForm(false);
      } catch (refreshError) {
        setSuccessWarning('El nuevo ticket fue generado y el PDF se abrió, pero no fue posible actualizar la pantalla.');
        setRefreshPending(true);
      }
    } catch (error) {
      if (!commandSucceeded) closePdfPopup(pdfWindow);
      if (isTicketChangedError(error)) {
        operationIdRef.current = null;
        setEditing(false);
        setTicketEdited(false);
        try {
          if (handleUpdateData) await handleUpdateData();
        } catch (refreshError) {
          setRefreshPending(true);
        }
      }
      setErrorMessage(canonicalTicketErrorMessage(error));
    } finally {
      submitRef.current = false;
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={openTicketEntradaForm}
        onClose={() => setOpenTicketEntradaForm(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Ticket de Entrada</DialogTitle>
        <DialogContent>
          {errorMessage && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage}</Alert>}
          {successWarning && <Alert severity="warning" sx={{ mt: 1 }}>{successWarning}</Alert>}
          {editing && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Se generará un nuevo ticket. El ticket anterior permanecerá disponible en el historial.
            </Alert>
          )}
          <Typography variant="body1" gutterBottom sx={{ pt: 2 }}><strong>Empleado:</strong> {user?.nombre}</Typography>
          <Typography variant="body1" gutterBottom><strong>Fecha Inicio:</strong> {fechaInicio}</Typography>
          <Typography variant="body1" gutterBottom><strong>Fecha Final:</strong> {fechaFin}</Typography>

          <InputLabel sx={{ pt: 1 }}>Cliente</InputLabel>
          <TextField
            fullWidth
            value={dataSelectedCliente ? `${dataSelectedCliente.nombre} ${dataSelectedCliente.apellidos || ''}`.trim() : ''}
            disabled
          />
          <Button
            variant="outlined"
            sx={{ mt: 1 }}
            onClick={() => setOpenDialogClientes(true)}
            disabled={!areUserAdmin || loading || (Boolean(currentTicket) && !editing)}
          >
            Buscar Cliente
          </Button>

          <TextField
            label="Valor a Pagar"
            variant="outlined"
            type="text"
            inputMode="decimal"
            fullWidth
            margin="normal"
            value={valorPagado}
            onChange={(event) => {
              setValorPagado(event.target.value);
              setTicketEdited(true);
            }}
            disabled={!areUserAdmin || loading || (Boolean(currentTicket) && !editing)}
          />
          {!areUserAdmin && currentTicket && (
            <Typography variant="caption" color="text.secondary" display="block">
              Solo los administradores pueden modificar este ticket.
            </Typography>
          )}
          <InputLabel sx={{ pt: 2 }}>Observaciones</InputLabel>
          <TextField
            autoFocus
            fullWidth
            value={monthlyTicketObservationsInputValue(selectedObservaciones)}
            placeholder="Nada"
            onChange={(event) => {
              setSelectedObservaciones(event.target.value);
              setTicketEdited(true);
            }}
            inputProps={{ maxLength: 200 }}
            disabled={loading || (Boolean(currentTicket) && !editing)}
          />
          {refreshPending ? (
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              disabled={loading}
              onClick={async () => {
                setLoading(true);
                try {
                  if (handleUpdateData) await handleUpdateData();
                  setRefreshPending(false);
                  setOpenTicketEntradaForm(false);
                } catch (error) {
                  setErrorMessage('No fue posible actualizar la pantalla. Intenta nuevamente.');
                } finally {
                  setLoading(false);
                }
              }}
            >
              Actualizar datos
            </Button>
          ) : currentTicket && !editing ? (
            <>
              <Button variant="contained" onClick={handleCrearTicket} disabled={loading} sx={{ mt: 2 }}>
                {loading ? <CircularProgress size={24} /> : 'Reimprimir'}
              </Button>
              {areUserAdmin && !currentTicket.anulado && (
                <Button
                  variant="outlined"
                  disabled={loading}
                  sx={{ mt: 2, ml: 1 }}
                  onClick={() => {
                    operationIdRef.current = createTicketOperationId();
                    setFechaInicio(
                      latestRenewal?.fecha_entrada
                      ?? data?.fecha_inicio
                      ?? ''
                    );
                    setFechaFin(
                      latestRenewal?.fecha_salida
                      ?? data?.fecha_final
                      ?? ''
                    );
                    setEditing(true);
                    setTicketEdited(false);
                    setErrorMessage('');
                  }}
                >
                  Modificar ticket
                </Button>
              )}
            </>
          ) : (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCrearTicket}
                disabled={loading || (Boolean(currentTicket) && !ticketEdited)}
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : currentTicket ? 'Confirmar' : 'Imprimir Ticket de Entrada'}
              </Button>
              {currentTicket && editing && !ticketEdited && !loading && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Modifique al menos un campo para confirmar.
                </Typography>
              )}
              {currentTicket && (
                <Button
                  variant="outlined"
                  disabled={loading}
                  sx={{ mt: 2, ml: 1 }}
                  onClick={() => {
                    restoreCurrentTicket();
                    setEditing(false);
                    operationIdRef.current = null;
                    setErrorMessage('');
                  }}
                >
                  Cancelar
                </Button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDialogClientes}
        onClose={() => setOpenDialogClientes(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Buscar Cliente</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Buscar por nombre, documento..."
            value={searchText}
            onChange={(event) => {
              setSearchText(event.target.value);
              setCurrentPage(1);
            }}
            sx={{ mb: 2 }}
          />
          {loadingClientes ? <Typography>Cargando...</Typography> : clientesData.map((cliente) => (
            <div
              key={cliente.id}
              role="button"
              tabIndex={0}
              onClick={() => {
                setSelectedCliente(cliente.id);
                setDataSelectedCliente(cliente);
                setTicketEdited(true);
                setOpenDialogClientes(false);
              }}
              onKeyDown={() => {}}
              style={{
                padding: 10,
                borderBottom: '1px solid #ccc',
                cursor: 'pointer',
                backgroundColor: selectedCliente === cliente.id ? '#1976d2' : 'transparent',
                color: selectedCliente === cliente.id ? 'white' : 'inherit',
              }}
            >
              <strong>{cliente.nombre} {cliente.apellidos}</strong><br />
              {cliente.tipo_documento}: {cliente.documento}
            </div>
          ))}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
              <Button disabled={currentPage === 1} onClick={() => setCurrentPage((page) => page - 1)}>Anterior</Button>
              <Typography sx={{ mx: 2 }}>{currentPage} / {totalPages}</Typography>
              <Button disabled={currentPage === totalPages} onClick={() => setCurrentPage((page) => page + 1)}>Siguiente</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TicketEntradaForm;
